import React, { useRef, useState } from 'react';
import FamilyDialog from '../family/FamilyDialog';
import {
  callDecideFamilyChangeRequest,
  type DecideFamilyChangeRequestResult,
} from './decideFamilyChangeRequestClient';

/** Errors the server can report that an admin can resolve by providing an override reason and resending. */
const OVERRIDABLE_ERRORS = new Set(['AWAITING_CONSENT', 'TOO_MANY_PEOPLE']);

interface ApproveFamilyRequestDialogProps {
  requestId: string;
  onClose: () => void;
  onApproved: (message: string) => void;
}

/**
 * Approve confirmation for a FamilyChangeRequest (#127 slice 5). On the
 * first attempt no override reason is sent. If the server reports
 * AWAITING_CONSENT or TOO_MANY_PEOPLE, an override reason field appears and
 * the admin can resend the same decision with `adminOverrideReason`.
 */
const ApproveFamilyRequestDialog: React.FC<ApproveFamilyRequestDialogProps> = ({
  requestId,
  onClose,
  onApproved,
}) => {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [needsOverride, setNeedsOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');

  const handleApprove = async () => {
    setSubmitting(true);
    setServerError(null);
    try {
      const args: { requestId: string; decision: 'APPROVE'; adminOverrideReason?: string } = {
        requestId,
        decision: 'APPROVE',
      };
      if (needsOverride && overrideReason.trim()) {
        args.adminOverrideReason = overrideReason.trim();
      }
      const result: DecideFamilyChangeRequestResult = await callDecideFamilyChangeRequest(args);
      if (result.success) {
        onApproved(result.message || 'Request approved.');
        return;
      }
      if (OVERRIDABLE_ERRORS.has(result.error)) {
        setNeedsOverride(true);
      }
      setServerError(result.message || 'Could not approve this request. Please try again.');
    } catch (err: any) {
      setServerError(err?.message || 'Could not approve this request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FamilyDialog
      titleId="approve-family-request-title"
      title="Approve this request?"
      descriptionId="approve-family-request-desc"
      busy={submitting}
      onClose={onClose}
    >
      <p id="approve-family-request-desc" className="mt-2 text-sm text-gray-700">
        The family will be updated and the member notified.
      </p>

      {serverError && (
        <p role="alert" className="mt-3 rounded-md border border-red-200 bg-red-50 p-2 text-sm font-medium text-red-700">
          {serverError}
        </p>
      )}

      {needsOverride && (
        <div className="mt-3">
          <label
            htmlFor="approve-override-reason"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Reason to approve anyway *
          </label>
          <input
            id="approve-override-reason"
            type="text"
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
            className="min-h-[44px] w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-fishing-blue"
          />
        </div>
      )}

      <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button
          ref={confirmRef}
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-2 font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleApprove}
          disabled={submitting || (needsOverride && !overrideReason.trim())}
          className="min-h-[44px] rounded-lg bg-fishing-green px-4 py-2 font-semibold text-white hover:bg-green-600 disabled:opacity-50"
        >
          {submitting
            ? 'Approving…'
            : needsOverride
              ? 'Approve anyway'
              : 'Approve'}
        </button>
      </div>
    </FamilyDialog>
  );
};

export default ApproveFamilyRequestDialog;
